import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  IconButton,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Avatar,
  Alert,
  Chip,
  Grid,
  InputAdornment,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import logo from "./assets/logo.png";
import NotificationSnackbar from "./NotificationBar";

function Ticket() {
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    idNumber: "",
    phoneNumber: "",
    email: "",
    categoryId: "",
    description: "",
    file: "",
    role: "",
  });
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [identityInput, setIdentityInput] = useState("");
  const [identityLoading, setIdentityLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleVerifyID = async (idValue) => {
    const idNumber = idValue || formData.idNumber;
    if (!idNumber || idNumber.length !== 16) return;

    setVerifying(true);
    setVerificationError("");

    try {
      const res = await axios.get(`/api/support/verify-nid-number/${idNumber}`);
      const { foreName, surnames } = res.data;
      setFormData((prev) => ({
        ...prev,
        fullName: ` ${foreName} ${surnames}`,
      }));
    } catch (error) {
      setVerificationError(
        "Reba ko wanditse neza Indangamuntu yawe / Check well if your ID number is correct",
      );
      setFormData((prev) => ({ ...prev, fullName: "" }));
    } finally {
      setVerifying(false);
    }
  };

  const verifyIdentity = async () => {
    if (!identityInput.trim()) {
      handleSnackbarOpen(
        "Please enter email, phone number, or ID number",
        "error",
      );
      return;
    }

    setIdentityLoading(true);
    try {
      let params = {};
      if (identityInput.includes("@")) {
        params = { email: identityInput };
      } else if (/^\d+$/.test(identityInput)) {
        if (identityInput.length === 11 || identityInput.length === 16) {
          params = { idNumber: identityInput };
        } else {
          params = { phoneNumber: identityInput };
        }
      } else {
        params = { phoneNumber: identityInput };
      }

      const response = await axios.get("/api/support/user-info", { params });

      if (response.data) {
        setUserInfo(response.data);
        setModalOpen(true);
        setFormData((prev) => ({
          ...prev,
          email: response.data.email || prev.email,
          phoneNumber: response.data.phoneNumber || prev.phoneNumber,
          idNumber: response.data.idNumber || prev.idNumber,
        }));
      } else {
        handleSnackbarOpen(
          "User not found with the provided information",
          "error",
        );
      }
    } catch (error) {
      console.error("Identity verification error:", error);
      handleSnackbarOpen(
        error.response?.status === 404
          ? "User not found with the provided information"
          : "Error verifying identity",
        "error",
      );
    } finally {
      setIdentityLoading(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true);
        const { data } = await axios.get("/api/support/categories", {
          method: "GET",
          credentials: "include",
        });
        setCategories(data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let cleanedValue = value;

    if (name === "idNumber") {
      cleanedValue = value.replace(/\s+/g, "");
    }
    if (name === "email") {
      const emailRegex = /^(?!.*\.\.)[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(cleanedValue) && cleanedValue !== "") {
        setEmailError("Invalid email format");
      } else {
        setEmailError("");
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));

    if (name === "idNumber" && cleanedValue.length === 16) {
      handleVerifyID(cleanedValue);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        file,
      }));
    }
  };

  const handleRoleChange = (event) => {
    const selectedRole = event.target.value;
    setFormData({
      ...formData,
      role: selectedRole,
      idNumber: selectedRole === "Applicant" ? formData.idNumber : "",
      email: selectedRole === "Applicant" ? formData.email : "",
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    const formDataToSend = new FormData();
    formDataToSend.append("phoneNumber", formData.phoneNumber);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("idNumber", formData.idNumber);
    formDataToSend.append("ticketDescription", formData.description);
    formDataToSend.append("categoryId", formData.categoryId);
    formDataToSend.append("role", formData.role);

    if (formData.file) {
      formDataToSend.append("file", formData.file);
    }
    try {
      const response = await axios.post(
        "/api/support/create-ticket",
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const message =
        typeof response.data.message === "string"
          ? response.data.message
          : response.data.message?.Message || "Ticket created successfully!";

      handleSnackbarOpen(message, "success");
      setFormData({
        phoneNumber: "",
        email: "",
        idNumber: "",
        description: "",
        categoryId: "",
        role: "",
        file: null,
      });
      setTimeout(() => navigate("/"), 3000);
    } catch (error) {
      let errorMsg = "An error occurred!";
      if (error.response?.data?.message) {
        errorMsg =
          typeof error.response.data.message === "string"
            ? error.response.data.message
            : error.response.data.message?.Message || errorMsg;
      } else if (error.message) {
        errorMsg = error.message;
      }
      handleSnackbarOpen(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTicket = () => {
    navigate("/");
  };

  const importantMessages = [
    "Uzuza neza imyirondoro isabwa muri iyi Form",
    "Imyirondoro ishyirwa muri iyi Form niy'umuntu ukeneye ubufasha",
    "Mbere yo kohereza banza urebe neza ko ibyo wanditse ari ukuri",
    "Ibisubizo by'umuntu wasabye ubufasha byoherezwa kuri email mwaduhaye muri iyo form",
  ];

  const maskPhoneNumber = (phone) => {
    if (!phone || phone.length < 7) return phone;
    return phone.slice(0, 3) + "****" + phone.slice(7);
  };

  const maskIDNumber = (idnumber) => {
    if (!idnumber || idnumber.length < 16) return idnumber;
    return "*************" + idnumber.slice(-3);
  };

  const maskedPhone = userInfo?.phoneNumber
    ? maskPhoneNumber(userInfo.phoneNumber)
    : "Your phone number is not registered";

  const maskedidNumber = userInfo?.idNumber
    ? maskIDNumber(userInfo.idNumber)
    : "Your phone number is not registered";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #0a2e52 0%, #1565C0 100%)",
        padding: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={24}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          borderRadius: 4,
          overflow: "hidden",
          width: "100%",
          maxWidth: 1000,
          minHeight: 650,
        }}
      >
        {/* Left Side: Instructions & Info */}
        <Box
          sx={{
            width: { xs: "100%", md: "40%" },
            background: "linear-gradient(180deg, #0d47a1 0%, #0a2e52 100%)",
            color: "white",
            p: { xs: 3, md: 5 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                width: "48px",
                height: "48px",
                marginRight: "16px",
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "4px",
              }}
            />
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, letterSpacing: 1 }}
              >
                Support Ticket
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                We're here to assist you
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: 3,
              p: 3,
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <WarningAmberIcon sx={{ color: "#ffb74d", mr: 1.5 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#ffb74d" }}
              >
                ICYITONDERWA
              </Typography>
            </Box>
            <Box
              component="ul"
              sx={{
                pl: 0,
                m: 0,
                listStyle: "none",
                "& li": { mb: 2, display: "flex", alignItems: "flex-start" },
              }}
            >
              {importantMessages.map((message, index) => (
                <Box component="li" key={index}>
                  <Box
                    sx={{
                      width: "6px",
                      height: "6px",
                      backgroundColor: "#ffb74d",
                      borderRadius: "50%",
                      mt: "8px",
                      mr: "12px",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: 1.5, opacity: 0.95 }}
                  >
                    {message}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* User Guide Link Section */}
          <Box
            component="a"
            href="https://support.ippis.rw/articles/250708"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              mt: 4,
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "rgba(59, 130, 246, 0.15)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: "12px",
              p: 2,
              textDecoration: "none",
              color: "white",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(59, 130, 246, 0.25)",
                borderColor: "rgba(59, 130, 246, 0.5)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                p: 1,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MenuBookIcon sx={{ color: "#93c5fd" }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#fffffffff" }}
              >
                Need to know more about how to request support? / Ukeneye
                kumenya uko wasaba Ubufasha?
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255, 193, 7)" }}>
                Read the guide on how to request support
              </Typography>
            </Box>
            <OpenInNewIcon
              sx={{ color: "rgba(255,255,255,0.4)", fontSize: 20 }}
            />
          </Box>
        </Box>

        {/* Right Side: Form */}
        <Box
          sx={{
            width: { xs: "100%", md: "60%" },
            bgcolor: "#ffffff",
            p: { xs: 3, md: 5 },
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <IconButton
            onClick={handleCloseTicket}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "#94a3b8",
              "&:hover": { backgroundColor: "#f1f5f9", color: "#0f172a" },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#1e293b", mb: 1 }}
          >
            Submit a Request
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mb: 4 }}>
            Please fill in the details below so we can assist you appropriately.
          </Typography>

          <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
            <Grid container spacing={2}>
              {/* Select Role */}
              <Grid item xs={12}>
                <InputLabel
                  sx={{
                    fontWeight: 600,
                    color: "#334155",
                    mb: 0.75,
                    fontSize: "0.9rem",
                    letterSpacing: "0.3px",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                  }}
                >
                  Select Role
                </InputLabel>
                <Select
                  fullWidth
                  name="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        borderRadius: "12px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
                        border: "1px solid #e2e8f0",
                        "& .MuiList-root": {
                          padding: "8px",
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#ffffff",
                    transition: "all 0.2s ease-in-out",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    color: "#1e293b",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e1",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#94a3b8",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                      borderWidth: "2px",
                    },
                    "& .MuiSelect-icon": {
                      color: "#64748b",
                      transition: "transform 0.2s",
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    disabled
                    sx={{
                      fontStyle: "italic",
                      color: "#94a3b8",
                      fontSize: "0.95rem",
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                    }}
                  >
                    Choose your role...
                  </MenuItem>

                  {["Applicant", "Public Servant"].map((roleOption) => (
                    <MenuItem
                      key={roleOption}
                      value={roleOption}
                      sx={{
                        borderRadius: "6px",
                        mb: 0.5,
                        fontSize: "0.95rem",
                        fontFamily: "'Inter', 'Roboto', sans-serif",
                        fontWeight: formData.role === roleOption ? 600 : 400,
                        color:
                          formData.role === roleOption ? "#1d4ed8" : "#334155",
                        backgroundColor:
                          formData.role === roleOption
                            ? "#eff6ff"
                            : "transparent",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          color: "#0f172a",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "#eff6ff",
                          "&:hover": {
                            backgroundColor: "#dbeafe",
                          },
                        },
                      }}
                    >
                      {roleOption}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {/* Conditional Applicant Fields */}
              {formData.role === "Applicant" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <InputLabel
                      sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}
                    >
                      Phone Number
                    </InputLabel>
                    <TextField
                      fullWidth
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="e.g. 078XXXXXXX"
                      InputProps={{ sx: { borderRadius: "8px" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel
                      sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}
                    >
                      Email Address
                    </InputLabel>
                    <TextField
                      fullWidth
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!emailError}
                      helperText={emailError}
                      placeholder="e.g. email@example.com"
                      InputProps={{ sx: { borderRadius: "8px" } }}
                    />
                  </Grid>

                  {formData.email.length > 0 && (
                    <Grid item xs={12}>
                      <Fade in={formData.email.length > 0}>
                        <Alert
                          severity="info"
                          icon={<InfoOutlinedIcon />}
                          sx={{ borderRadius: "8px", alignItems: "center" }}
                        >
                          Iyi Email muduhaye murebe ko yanditse neza kuko niyo
                          turabashyirira muri system. Niba mutacyibuka password,
                          turabagira inama yo gukora email nshya.
                        </Alert>
                      </Fade>
                    </Grid>
                  )}
                </>
              )}

              {/* ID Number */}
              <Grid item xs={12}>
                <InputLabel
                  sx={{
                    fontWeight: 600,
                    color: "#334155",
                    mb: 0.75,
                    fontSize: "0.9rem",
                    letterSpacing: "0.3px",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                  }}
                >
                  ID Number
                </InputLabel>
                <TextField
                  fullWidth
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  placeholder="16-digit ID number"
                  error={!!verificationError}
                  helperText={
                    verificationError ||
                    (formData.fullName && `✓ Name: ${formData.fullName}`)
                  }
                  FormHelperTextProps={{
                    sx: {
                      color:
                        !verificationError && formData.fullName
                          ? "#16a34a !important"
                          : undefined,
                      fontWeight:
                        !verificationError && formData.fullName ? 600 : 400,
                      fontSize: "0.85rem",
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                      mt: 0.75,
                    },
                  }}
                  InputProps={{
                    sx: {
                      borderRadius: "8px",
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                      transition: "all 0.3s ease",
                      backgroundColor:
                        !verificationError && formData.fullName
                          ? "#f0fdf4"
                          : "#ffffff",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          !verificationError && formData.fullName
                            ? "#16a34a !important"
                            : undefined,
                      },
                    },
                    endAdornment: formData.idNumber.length === 16 && (
                      <InputAdornment position="end">
                        {verifying ? (
                          <CircularProgress
                            size={20}
                            sx={{ color: "#3b82f6" }}
                          />
                        ) : formData.fullName ? (
                          <CheckCircleIcon sx={{ color: "#16a34a" }} />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Module / Category */}
              <Grid item xs={12}>
                <InputLabel
                  sx={{
                    fontWeight: 600,
                    color: "#334155",
                    mb: 0.75,
                    fontSize: "0.9rem",
                    letterSpacing: "0.3px",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                  }}
                >
                  Module / Category
                </InputLabel>
                <Select
                  fullWidth
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  displayEmpty
                  disabled={!formData.role}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 1,
                        borderRadius: "12px",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
                        border: "1px solid #e2e8f0",
                        "& .MuiList-root": {
                          padding: "8px",
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: formData.role ? "#ffffff" : "#f8fafc",
                    transition: "all 0.2s ease-in-out",
                    fontFamily: "'Inter', 'Roboto', sans-serif",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    color: "#1e293b",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#cbd5e1",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#94a3b8",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                      borderWidth: "2px",
                    },
                    "& .MuiSelect-icon": {
                      color: "#64748b",
                      transition: "transform 0.2s",
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    disabled
                    sx={{
                      fontStyle: "italic",
                      color: "#94a3b8",
                      fontSize: "0.95rem",
                      fontFamily: "'Inter', 'Roboto', sans-serif",
                    }}
                  >
                    Select a module...
                  </MenuItem>

                  {isLoading ? (
                    <MenuItem disabled sx={{ justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} sx={{ color: "#3b82f6" }} />
                    </MenuItem>
                  ) : (
                    categories
                      .filter((category) => {
                        if (formData.role === "Applicant") {
                          return category.categoryName === "RECRUITMENT";
                        }
                        return category.isOnHomePage === true;
                      })
                      .map((category) => (
                        <MenuItem
                          key={category.id}
                          value={category.id}
                          sx={{
                            borderRadius: "6px",
                            mb: 0.5,
                            fontSize: "0.95rem",
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            fontWeight:
                              formData.categoryId === category.id ? 600 : 400,
                            color:
                              formData.categoryId === category.id
                                ? "#1d4ed8"
                                : "#334155",
                            backgroundColor:
                              formData.categoryId === category.id
                                ? "#eff6ff"
                                : "transparent",
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                              color: "#0f172a",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#eff6ff",
                              "&:hover": {
                                backgroundColor: "#dbeafe",
                              },
                            },
                          }}
                        >
                          {category.categoryName}
                        </MenuItem>
                      ))
                  )}
                </Select>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <InputLabel sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}>
                  Description
                </InputLabel>
                <TextField
                  fullWidth
                  name="description"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your issue in detail (min 50 characters)..."
                  error={
                    formData.description.length > 0 &&
                    formData.description.length < 50
                  }
                  helperText={
                    formData.description.length > 0 &&
                    formData.description.length < 50
                      ? `Minimum 50 characters required (${formData.description.length}/50)`
                      : formData.description.length >= 50
                        ? `${formData.description.length} characters`
                        : ""
                  }
                  InputProps={{ sx: { borderRadius: "8px" } }}
                />
              </Grid>

              {/* File Attachment */}
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      color: "#475569",
                      borderColor: "#cbd5e1",
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        borderColor: "#94a3b8",
                      },
                    }}
                  >
                    Attach File
                    <input
                      type="file"
                      name="file"
                      hidden
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                    />
                  </Button>
                  {formData.file && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                      }}
                    >
                      {formData.file.name}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={
              loading ||
              formData.description.length < 50 ||
              (formData.role === "Applicant" &&
                (!!emailError || !formData.email)) ||
              !formData.role ||
              !formData.categoryId
            }
            endIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              mt: 4,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0 4px 14px 0 rgba(13, 71, 161, 0.39)",
              "&:hover": { boxShadow: "0 6px 20px rgba(13, 71, 161, 0.23)" },
            }}
          >
            {loading ? "Submitting Request..." : "Submit Support Ticket"}
          </Button>
        </Box>
      </Paper>

      {/* User Info Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: { xs: 0, sm: 3 }, m: { xs: 0, sm: 2 } },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#0d47a1",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Information
            </Typography>
          </Box>
          <IconButton
            onClick={() => setModalOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {userInfo && (
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: "#1976d2",
                    fontSize: "1.5rem",
                  }}
                >
                  {userInfo.firstName?.[0] ?? "U"}
                  {userInfo.lastName?.[0] ?? ""}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {userInfo.firstName ?? "Name not"}{" "}
                    {userInfo.lastName ?? "registered"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    System ID: {userInfo.id || "N/A"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Grid container spacing={3}>
                <Grid item xs={12} display="flex" gap={2}>
                  <BadgeIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      ID Number
                    </Typography>
                    <Typography variant="body1">{maskedidNumber}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} display="flex" gap={2}>
                  <EmailIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Email Address
                    </Typography>
                    <Typography variant="body1">
                      {userInfo.email ?? "Not registered"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} display="flex" gap={2}>
                  <PhoneIcon color="primary" />
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Phone Number
                    </Typography>
                    <Typography variant="body1">{maskedPhone}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mt: 4, borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  ICYITONDERWA
                </Typography>
                Niba email cyangwa Phone Number usanzemo utakibikoresha
                twandikire tugufashe kubihindura, ushyiremo amakuru mashya
                ushaka ko tugushyirira muri profile yawe.
                <br />
                <a
                  href="https://support.ippis.rw/CreateTicket"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    fontWeight: 600,
                    display: "inline-block",
                    marginTop: "8px",
                  }}
                >
                  Kanda hano usabe ubufasha
                </a>
              </Alert>

              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 3, py: 1.5, borderRadius: 2, textTransform: "none" }}
                onClick={() => setModalOpen(false)}
              >
                Close Profile
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default Ticket;
